import {
  canAddUsersForLicense,
  getFeatureFromLicense,
  hasAssetGroupsInLicense,
  hasValidFeatureInLicense,
  shouldDisplayLicenseWarning,
} from '@/licenseManagement/licenseManagement.utilities';
import { type SetStoreState, createStore, createStoreInstance } from '@/stores/createStore';
import type { Feature } from '@/licenseManagement/licenseManagement.constants';
import type { LicenseData, LicenseManagementState, LicenseStatus } from '@/licenseManagement/licenseManagement.types';

const initialState: LicenseManagementState = {
  licenseStatus: undefined,
  license: {
    additionalFeatures: [],
  },
  showOfficeHours: false,
};

type LicenseManagementActions = ReturnType<typeof licenseManagementActionsFactory>;

const licenseManagementActionsFactory = (set: SetStoreState<LicenseManagementState>) => ({
  setLicense: (license: LicenseData) =>
    set((draft) => {
      draft.license = license;
    }),
  setLicenseStatus: (status: LicenseStatus) =>
    set((draft) => {
      draft.licenseStatus = status;
    }),
  setShowOfficeHours: (show: boolean) =>
    set((draft) => {
      draft.showOfficeHours = show;
    }),
  reset: () => set(() => initialState),
});

const [LicenseManagementStoreProvider, useStore, useSelector] = createStore(
  'LicenseManagement',
  initialState,
  licenseManagementActionsFactory,
);

export { LicenseManagementStoreProvider };
export const useLicenseStatus = () => useSelector((state) => state.licenseStatus);
export const useLicense = () => useSelector((state) => state.license);
export const useContractNumber = () => useSelector((state) => state.license.contractNumber);
export const useLicenseConfiguration = () => useSelector((state) => state.license?.configuration);
export const useShowOfficeHours = () => useSelector((state) => state.showOfficeHours);
export const useIsTrial = () => useSelector((state) => state.license?.level === 'Trial');
export const useLicenseManagementActions = () => useStore().actions;

export const useDisplayLicenseWarning = () => useSelector((state) => shouldDisplayLicenseWarning(state.licenseStatus));

export const useCanAddUsers = () => useSelector((state) => canAddUsersForLicense(state.license));

export const useHasValidFeature = (feature: Feature) =>
  useSelector((state) => hasValidFeatureInLicense(state.license, feature));

export const useHasAssetGroups = () => useSelector((state) => hasAssetGroupsInLicense(state.license));

export const useGetFeature = (name: string) => useSelector((state) => getFeatureFromLicense(state.license, name));

/**
 * @deprecated Test-only helper. Use test utils instead.
 *
 * Creates a test-specific license management store instance.
 */
export function createTestLicenseManagementStore() {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('createTestLicenseManagementStore() should only be used in tests.');
  }

  return createStoreInstance(initialState, licenseManagementActionsFactory);
}
/**
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores.
 * DO NOT use in new code.
 */
export function legacyCreateLicenseManagementStore() {
  return createStoreInstance<LicenseManagementState, LicenseManagementActions>(
    initialState,
    licenseManagementActionsFactory,
  );
}
